import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";

import styles from "./styles.module.scss";
import {
  PhoneRule,
  digitsOnly,
  defaultPhoneRule,
  findRuleForNumber,
  flagIsoCode,
  hasLiveError,
  maxTypedLength,
  toFullNumber,
  toNationalNumber,
} from "../../../setup/util/phoneRules";

interface Props {
  /** Enabled countries, from the phone rules. */
  rules: PhoneRule[];
  /** The full number ("+<calling code><national number>") or "". */
  value: string;
  onChange: (fullNumber: string) => void;
  /** Error from the form (submit) or the service. */
  errorMessage?: string;
  /** Shown while typing once the number can no longer become valid. */
  invalidMessage?: string;
  placeholder?: string;
  countryLabel?: string;
  disabled?: boolean;
}

const FLAG_URL = "https://waffy-files.s3.eu-west-1.amazonaws.com/flags/";

/** Flag as an image (emoji flags show as letters on Windows), falling back to the emoji. */
const Flag = ({ rule }: { rule: PhoneRule }) => {
  const [failed, setFailed] = useState(false);
  const iso = flagIsoCode(rule);
  if (!iso || failed) {
    return (
      <span className={styles.flag}>
        <span className={styles.flagBadge}>
          {flagIsoCode(rule)?.toUpperCase() || "+" + rule.countryCode}
        </span>
      </span>
    );
  }
  return (
    <span className={styles.flag}>
      <img
        className={styles.flagImg}
        src={`${FLAG_URL}${iso}.svg`}
        alt=""
        onError={() => setFailed(true)}
      />
    </span>
  );
};

/**
 * Country picker plus the national number, behaving like the Waffy ID registration form: digits
 * only (Arabic digits are converted), the calling code shown inside the field, the length capped by
 * the country's rule, a leading local zero allowed where the rule allows it, and an error shown
 * while typing only once the number cannot become valid. The value reported is the full number with
 * the calling code (without the zero), ready for the API.
 */
const PhoneRuleInput: React.FC<Props> = ({
  rules,
  value,
  onChange,
  errorMessage,
  invalidMessage,
  placeholder,
  countryLabel,
  disabled,
}) => {
  const initialRule = findRuleForNumber(rules, value) ?? defaultPhoneRule(rules);
  const [countryCode, setCountryCode] = useState(initialRule?.countryCode);
  const [typed, setTyped] = useState(
    initialRule && value ? toNationalNumber(initialRule, value) : ""
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const caretRef = useRef<number | null>(null);
  const lastEmitted = useRef(value);
  const rootRef = useRef<HTMLDivElement>(null);

  // Follow the form when it is reset or filled from outside (edit mode, closing the dialog), but
  // not when the change is the one this field just reported (that would drop a typed local zero).
  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    const rule = findRuleForNumber(rules, value);
    if (rule) {
      setCountryCode(rule.countryCode);
      setTyped(toNationalNumber(rule, value));
    } else if (!value) {
      // Reset from outside (a new dialog): back to the default country, like the registration page.
      setTyped("");
      setCountryCode(defaultPhoneRule(rules)?.countryCode);
    }
  }, [value, rules]);

  // Keep the caret where the user was typing after the value is rewritten.
  useLayoutEffect(() => {
    if (caretRef.current != null && inputRef.current) {
      inputRef.current.setSelectionRange(caretRef.current, caretRef.current);
      caretRef.current = null;
    }
  });

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuOpen]);

  const rule = rules.find((r) => r.countryCode === countryCode) ??
    defaultPhoneRule(rules);
  if (!rule) return null;

  const emit = (next: PhoneRule, nextTyped: string) => {
    const full = toFullNumber(next, nextTyped);
    lastEmitted.current = full;
    onChange(full);
  };

  const liveError = hasLiveError(rule, typed);
  const message = errorMessage || (liveError ? invalidMessage : undefined);
  const showError = !!message || liveError;

  return (
    <div className={styles.outer} ref={rootRef}>
      <div
        className={classNames(styles.wrapper, {
          [styles.hasError]: showError,
          [styles.isDisabled]: disabled,
        })}>
        <div className={styles.countrySelect}>
          <button
            type="button"
            className={styles.countryButton}
            aria-label={countryLabel}
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              setMenuOpen((open) => !open);
            }}>
            <Flag rule={rule} />
            <svg
              className={styles.chevron}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          {menuOpen && (
            <div className={styles.menu} role="listbox">
              {rules.map((r) => (
                <button
                  type="button"
                  key={r.countryCode}
                  role="option"
                  aria-selected={r.countryCode === rule.countryCode}
                  className={styles.option}
                  onClick={() => {
                    setCountryCode(r.countryCode);
                    // Rules differ per country; a half-valid number carried across misleads.
                    setTyped("");
                    emit(r, "");
                    setMenuOpen(false);
                    inputRef.current?.focus();
                  }}>
                  <Flag rule={r} />
                  <span>{r.countryNameAr || r.countryName}</span>
                  <span className={styles.optionCode}>+{r.countryCode}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <span className={styles.prefix}>+{rule.countryCode}</span>
        <input
          ref={inputRef}
          className={styles.number}
          dir="ltr"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder={placeholder}
          value={typed}
          maxLength={maxTypedLength(rule)}
          disabled={disabled}
          onChange={(event) => {
            const raw = event.target.value;
            const caret = event.target.selectionStart;
            const kept =
              caret == null ? null : digitsOnly(raw.slice(0, caret)).length;
            let next = digitsOnly(raw);
            next = next.slice(0, maxTypedLength(rule));
            if (next !== raw && kept != null) {
              caretRef.current = Math.min(kept, next.length);
            }
            setTyped(next);
            emit(rule, next);
          }}
        />
      </div>
      {message && <p className={styles.errorText}>{message}</p>}
    </div>
  );
};

export default PhoneRuleInput;
